package fyi.moko.hotpl.ui.theme

import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.Font
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontVariation
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp
import hotpla.shared.generated.resources.Res
import hotpla.shared.generated.resources.be_vietnam_pro_regular
import hotpla.shared.generated.resources.be_vietnam_pro_semibold
import hotpla.shared.generated.resources.literata_italic_variable
import hotpla.shared.generated.resources.literata_variable
import org.jetbrains.compose.resources.Font as ResFont

/**
 * Warm Journal type system (docs/design-ref/.../warm_journal/DESIGN.md): Literata
 * (serif) for headlines/titles/journal text, Be Vietnam Pro (sans) for UI chrome.
 * Literata ships upstream as a variable font (no static per-weight files), so each
 * weight is one [Font] entry against the same file with a [FontVariation.Settings]
 * pin — Compose picks the right instance per weight without needing 4 separate files.
 */
@Composable
fun warmJournalLiterata(): FontFamily {
    // Font() resource loading is itself @Composable (it drives async load state), so
    // each instance must be requested directly in composition — not inside remember's
    // calculation lambda — before being handed to the plain FontFamily(...) factory.
    val regular = ResFont(Res.font.literata_variable, weight = FontWeight.Normal, style = FontStyle.Normal)
    val semiBold = ResFont(
        Res.font.literata_variable,
        weight = FontWeight.SemiBold,
        style = FontStyle.Normal,
        variationSettings = FontVariation.Settings(FontVariation.weight(600)),
    )
    val bold = ResFont(
        Res.font.literata_variable,
        weight = FontWeight.Bold,
        style = FontStyle.Normal,
        variationSettings = FontVariation.Settings(FontVariation.weight(700)),
    )
    val italic = ResFont(Res.font.literata_italic_variable, weight = FontWeight.Normal, style = FontStyle.Italic)
    return remember(regular, semiBold, bold, italic) { FontFamily(regular, semiBold, bold, italic) }
}

@Composable
fun warmJournalBeVietnamPro(): FontFamily {
    val regular = ResFont(Res.font.be_vietnam_pro_regular, weight = FontWeight.Normal, style = FontStyle.Normal)
    val semiBold = ResFont(Res.font.be_vietnam_pro_semibold, weight = FontWeight.SemiBold, style = FontStyle.Normal)
    return remember(regular, semiBold) { FontFamily(regular, semiBold) }
}

/**
 * Maps the mockups' named scale (display-lg, headline-md, body-md, body-lg /
 * "journal-entry", label-sm) onto M3's fixed 15-slot [Typography]. Sizes/line-heights
 * below are the mockup's token values where a slot has a direct match (headlineMedium
 * = headline-md exactly); other slots use the same two families at M3-adjacent sizes
 * since the mockups don't define every slot explicitly.
 */
@Composable
fun warmJournalTypography(): Typography {
    val literata = warmJournalLiterata()
    val sans = warmJournalBeVietnamPro()
    return Typography(
        displayLarge = TextStyle(fontFamily = literata, fontWeight = FontWeight.Bold, fontSize = 40.sp, lineHeight = 48.sp, letterSpacing = (-0.8).sp),
        displayMedium = TextStyle(fontFamily = literata, fontWeight = FontWeight.Bold, fontSize = 36.sp, lineHeight = 44.sp),
        displaySmall = TextStyle(fontFamily = literata, fontWeight = FontWeight.Bold, fontSize = 32.sp, lineHeight = 40.sp, letterSpacing = (-0.64).sp),
        headlineLarge = TextStyle(fontFamily = literata, fontWeight = FontWeight.Bold, fontSize = 32.sp, lineHeight = 38.sp),
        headlineMedium = TextStyle(fontFamily = literata, fontWeight = FontWeight.SemiBold, fontSize = 24.sp, lineHeight = 31.sp),
        headlineSmall = TextStyle(fontFamily = literata, fontWeight = FontWeight.SemiBold, fontSize = 20.sp, lineHeight = 26.sp),
        titleLarge = TextStyle(fontFamily = literata, fontWeight = FontWeight.SemiBold, fontSize = 18.sp, lineHeight = 24.sp),
        titleMedium = TextStyle(fontFamily = literata, fontWeight = FontWeight.SemiBold, fontSize = 16.sp, lineHeight = 22.sp),
        titleSmall = TextStyle(fontFamily = literata, fontWeight = FontWeight.SemiBold, fontSize = 14.sp, lineHeight = 20.sp),
        bodyLarge = TextStyle(fontFamily = literata, fontWeight = FontWeight.Normal, fontSize = 17.sp, lineHeight = 31.sp),
        bodyMedium = TextStyle(fontFamily = sans, fontWeight = FontWeight.Normal, fontSize = 16.sp, lineHeight = 24.sp),
        bodySmall = TextStyle(fontFamily = sans, fontWeight = FontWeight.Normal, fontSize = 14.sp, lineHeight = 20.sp),
        labelLarge = TextStyle(fontFamily = sans, fontWeight = FontWeight.SemiBold, fontSize = 14.sp, lineHeight = 20.sp),
        labelMedium = TextStyle(fontFamily = sans, fontWeight = FontWeight.SemiBold, fontSize = 13.sp, lineHeight = 16.sp, letterSpacing = 0.52.sp),
        labelSmall = TextStyle(fontFamily = sans, fontWeight = FontWeight.SemiBold, fontSize = 11.sp, lineHeight = 14.sp, letterSpacing = 0.44.sp),
    )
}
